import React from "react";
import { useL } from "@/lib/useL";
import { Link } from "react-router-dom";
import { X, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBilling } from "@/contexts/BillingContext";

// Shown when a user runs out of credits (or opens the credits meter manually).
export function BoosterPaywall({ open, onClose, reason = "manual" }) {
  const { t } = useTranslation();
  const L = useL();
  const { billing, credits } = useBilling();
  if (!open || !billing) return null;

  const cheapest = (billing.discovery_pack?.amount ?? billing.packs?.[0]?.amount ?? 2).toFixed(2).replace(".", ",");
  const headline =
    reason === "trial_closed" ? t("trial.closed_title") : reason === "quota_hit" ? L("Crédits épuisés", "Out of credits") : L("Besoin de plus de résumés ?", "Need more summaries?");
  const subline =
    reason === "trial_closed"
      ? t("trial.closed_body")
      : reason === "quota_hit"
      ? t("billing.credits_required")
      : L(`Il vous reste ${credits} crédit(s). 1 crédit = 1 résumé jusqu'à environ 20 pages.`, `You have ${credits} credit(s) left. 1 credit = 1 summary up to about 20 pages.`);

  return (
    <div
      data-testid="booster-paywall"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-card border border-[#002FA7] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="booster-paywall-close"
          className="absolute top-3 right-3 text-white/80 hover:text-white"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="bg-[#002FA7] text-white px-6 py-4 flex items-center gap-3">
          <Zap className="w-5 h-5 shrink-0" />
          <div className="font-mono uppercase tracking-[0.14em] text-[11px]">
            {t("billing.upgrade_page_marker")}
          </div>
        </div>
        <div className="px-6 py-5">
          <h2 className="text-lg font-semibold text-foreground mb-2">{headline}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5">{subline}</p>
          <Link
            to="/billing/upgrade"
            onClick={onClose}
            data-testid="booster-paywall-buy"
            className="w-full font-mono uppercase tracking-[0.14em] text-[12px] px-4 py-3 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors flex items-center justify-center gap-2"
          >
            {t("billing.paywall_cta", { price: cheapest })}
          </Link>
          <button
            type="button"
            onClick={onClose}
            data-testid="booster-paywall-cancel"
            className="w-full mt-2 font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border text-muted-foreground hover:text-foreground hover:bg-[#002FA7]/5 transition-colors"
          >
            {L("Plus tard", "Later")}
          </button>
          <div className="mt-3 text-[11px] text-muted-foreground text-center leading-relaxed">
            {L("Paiement sécurisé via Stripe · sans abonnement · crédits sans expiration", "Secure payment via Stripe · no subscription · credits never expire")}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BoosterPaywall;
